import { type KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as Popover from '@radix-ui/react-popover'
import { ArrowLeft, CalendarDays, Check, ClipboardPen, ScanSearch, Search, X } from 'lucide-react'

import DimensionIcon from './DimensionIcon'
import {
  defaultFilterValues,
  dimensionLabels,
  dimensionValueLabel,
  filterDimensions,
  filterSchema,
  type FilterValues,
  fixedOptions,
} from './model'

import styles from './FilterForm.module.scss'

import { attributionDimensions, type AttributionOverview } from '@/api/attributionObservability'
import Button from '@/components/Button'
import { normalizeMeetingId } from '@/pages/app/AttributionViewer/meetingId'
import type { ViewerMode } from '@/pages/app/AttributionViewer/mode'

type FilterField = (typeof filterDimensions)[number] | 'participant_source'
type FilterChoice = {
  kind: 'filter'
  field: FilterField
  value?: string
  label: string
  detail?: string
}
type MeetingChoice = {
  kind: 'meeting'
  meetingId: string
  mode: ViewerMode
  label: string
  detail: string
}
type Choice = FilterChoice | MeetingChoice
const meetingChoices = (meetingId: string): MeetingChoice[] => [
  {
    kind: 'meeting',
    meetingId,
    mode: 'inspect',
    label: 'Inspect meeting',
    detail: meetingId,
  },
  {
    kind: 'meeting',
    meetingId,
    mode: 'annotate',
    label: 'Annotate meeting',
    detail: meetingId,
  },
]
const choiceKey = (choice: Choice) =>
  choice.kind === 'meeting' ? `meeting:${choice.mode}` : `${choice.field}:${choice.value ?? ''}`
const sourceLabels = { canonical: 'Detected speakers', invited: 'Calendar attendees' }
const fields: FilterField[] = [...filterDimensions, 'participant_source']
const fieldLabel = (field: FilterField) =>
  field === 'participant_source' ? 'Participant source' : dimensionLabels[field]
const canNegate = (field: FilterField) => field !== 'participant_source'
const valueLabel = (field: FilterField, value: string) =>
  field === 'participant_source'
    ? sourceLabels[value as keyof typeof sourceLabels]
    : dimensionValueLabel(field, value)
const DAY_MS = 86_400_000
const dateRange = (days: number) => {
  const end = new Date().toISOString().slice(0, 10)
  return { start: new Date(new Date(end).getTime() - (days - 1) * DAY_MS).toISOString().slice(0, 10), end }
}

const recentPresets = [
  { days: 1, label: 'Past 24h' },
  { days: 3, label: 'Past 3 days' },
]

const DateRange = ({ values, onApply }: { values: FilterValues; onApply: (values: FilterValues) => void }) => {
  const [open, setOpen] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FilterValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: values,
  })
  const id = useId()
  const preset = [7, 30, 90].find((days) => {
    const range = dateRange(days)
    return range.start === values.start && range.end === values.end
  })
  const recentPreset = values.start.includes('T')
    ? recentPresets.find(
        ({ days }) => new Date(values.end).getTime() - new Date(values.start).getTime() === days * DAY_MS
      )
    : undefined
  const rangeLabel = `Date range (UTC): ${values.start} to ${values.end}`
  const apply = (range: Pick<FilterValues, 'start' | 'end'>) => {
    onApply({ ...values, ...range })
    setOpen(false)
  }
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        if (next) reset({ ...values, start: values.start.slice(0, 10), end: values.end.slice(0, 10) })
        setOpen(next)
      }}
    >
      <Popover.Trigger asChild>
        <button type="button" className={styles.dateTrigger} aria-label={rangeLabel} title={rangeLabel}>
          <CalendarDays size={15} aria-hidden="true" />
          {recentPreset?.label ?? (preset ? `Last ${preset} days` : `${values.start} – ${values.end}`)}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className={styles.datePopover} align="end" sideOffset={8} aria-label="Choose date range">
          <div className={styles.dateHeader}>
            <strong>Date range (UTC)</strong>
            <Popover.Close className={styles.iconButton} aria-label="Close date range">
              <X size={16} />
            </Popover.Close>
          </div>
          <div className={styles.presets}>
            {recentPresets.map(({ days, label }) => (
              <button
                type="button"
                key={days}
                aria-pressed={recentPreset?.days === days}
                onClick={() => {
                  const end = new Date()
                  apply({ start: new Date(end.getTime() - days * DAY_MS).toISOString(), end: end.toISOString() })
                }}
              >
                {label}
              </button>
            ))}
            {[7, 30, 90].map((days) => (
              <button type="button" key={days} aria-pressed={preset === days} onClick={() => apply(dateRange(days))}>
                Last {days} days
              </button>
            ))}
          </div>
          <form onSubmit={handleSubmit(({ start, end }) => apply({ start, end }))} className={styles.dateForm}>
            {(['start', 'end'] as const).map((name) => (
              <label key={name} htmlFor={`${id}-${name}`}>
                {name === 'start' ? 'Start date' : 'End date'}
                <input
                  id={`${id}-${name}`}
                  type="date"
                  {...register(name)}
                  aria-invalid={Boolean(errors[name])}
                  aria-describedby={errors[name] ? `${id}-${name}-error` : undefined}
                />
                {errors[name] && (
                  <span id={`${id}-${name}-error`} role="alert" className={styles.error}>
                    {errors[name]?.message}
                  </span>
                )}
              </label>
            ))}
            <p>Includes both dates. Choose up to 90 days.</p>
            <Button title="Apply dates" type="dark" htmlType="submit" onClick={() => undefined} />
          </form>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

const OperatorToggle = ({
  negated,
  label,
  onToggle,
  keepInputFocus = false,
}: {
  negated: boolean
  label: string
  onToggle: () => void
  keepInputFocus?: boolean
}) => (
  <button
    type="button"
    className={styles.operator}
    aria-label={label}
    aria-pressed={negated}
    onMouseDown={keepInputFocus ? (event) => event.preventDefault() : undefined}
    onClick={onToggle}
  >
    {negated ? '≠' : '='}
  </button>
)

interface FilterFormProps {
  values: FilterValues
  facets: AttributionOverview['facets']
  onApply: (values: FilterValues) => void
  onReset?: () => void
  onOpenChange?: (open: boolean) => void
  isLoadingOptions?: boolean
  /** Pasting a meeting ID or artifact URL offers Inspect/Annotate choices that call this. */
  onOpenMeeting?: (meetingId: string, mode: ViewerMode) => void
  /** Only the meeting lookup: no dimension filters or date range (non-admins have no metrics). */
  meetingLookupOnly?: boolean
}

const FilterForm = ({
  values,
  facets,
  onApply,
  onReset,
  onOpenChange,
  isLoadingOptions = false,
  onOpenMeeting,
  meetingLookupOnly = false,
}: FilterFormProps) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [field, setField] = useState<FilterField | null>(null)
  const [active, setActive] = useState(0)
  const [negated, setNegated] = useState(false)
  const [operatorShown, setOperatorShown] = useState(false)
  const closePicker = useCallback(() => {
    setOpen(false)
    setField(null)
    setQuery('')
    setActive(0)
    setNegated(false)
    setOperatorShown(false)
  }, [])
  const input = useRef<HTMLInputElement>(null)
  const wrapper = useRef<HTMLDivElement>(null)
  const openCallback = useRef(onOpenChange)
  openCallback.current = onOpenChange
  const listId = useId()
  useEffect(() => {
    openCallback.current?.(open)
    return () => {
      if (open) openCallback.current?.(false)
    }
  }, [open])
  useEffect(() => {
    const outside = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) closePicker()
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [closePicker])
  const choices = useMemo(() => {
    const optionsFor = (key: FilterField) =>
      key === 'participant_source'
        ? Object.keys(sourceLabels)
        : Array.from(
            new Set([...(fixedOptions[key] ?? facets[key] ?? []), 'unknown', ...(values[key] ? [values[key]] : [])])
          )
    const text = query.trim().toLowerCase()
    const meetingId = onOpenMeeting && !field ? normalizeMeetingId(query) : null
    if (meetingId) return meetingChoices(meetingId)
    if (meetingLookupOnly) return []
    let options: FilterChoice[] = []
    if (field) {
      options = optionsFor(field).map((value) => ({
        kind: 'filter',
        field,
        value,
        label: valueLabel(field, value),
      }))
      options = options.filter((option) => `${option.label} ${option.value}`.toLowerCase().includes(text))
      if (
        field.endsWith('_version') &&
        /^[a-zA-Z0-9._+-]{1,64}$/.test(query.trim()) &&
        !options.some((option) => option.value === query.trim())
      ) {
        options.push({
          kind: 'filter',
          field,
          value: query.trim(),
          label: `Use “${query.trim()}”`,
          detail: 'Exact version',
        })
      }
    } else {
      options = fields
        .filter((key) => !negated || canNegate(key))
        .filter((key) => fieldLabel(key).toLowerCase().includes(text))
        .map((key) => ({
          kind: 'filter',
          field: key,
          label: fieldLabel(key),
          detail: negated ? 'Exclude a value' : 'Choose a value',
        }))
      if (text)
        for (const key of fields) {
          if (negated && !canNegate(key)) continue
          options.push(
            ...optionsFor(key)
              .filter((value) => `${fieldLabel(key)} ${valueLabel(key, value)} ${value}`.toLowerCase().includes(text))
              .map((value) => ({
                kind: 'filter' as const,
                field: key,
                value,
                label: `${fieldLabel(key)}${negated ? ' is not' : ':'} ${valueLabel(key, value)}`,
              }))
          )
        }
    }
    return options.slice(0, 60)
  }, [facets, field, query, values, negated, onOpenMeeting, meetingLookupOnly])
  const activeIndex = Math.min(active, choices.length - 1)
  useEffect(() => {
    if (open && activeIndex >= 0)
      document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex, listId, open])
  const choose = (choice: Choice) => {
    setQuery('')
    setActive(0)
    if (choice.kind === 'meeting') {
      closePicker()
      onOpenMeeting?.(choice.meetingId, choice.mode)
      return
    }
    if (choice.value === undefined) {
      setField(choice.field)
      if (!canNegate(choice.field)) setNegated(false)
      return
    }
    const negate = values.negate.filter((dimension) => dimension !== choice.field)
    if (negated && canNegate(choice.field)) negate.push(choice.field as (typeof values.negate)[number])
    onApply({
      ...values,
      [choice.field]: choice.value,
      negate: attributionDimensions.filter((dimension) => negate.includes(dimension)),
    })
    closePicker()
  }
  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
      const delta = event.key === 'ArrowDown' ? 1 : -1
      setActive(open && choices.length ? (Math.max(activeIndex, 0) + delta + choices.length) % choices.length : 0)
    } else if (event.key === 'Enter' && open && choices[activeIndex]) {
      event.preventDefault()
      choose(choices[activeIndex])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closePicker()
    }
  }
  return (
    <section className={styles.container} aria-label={meetingLookupOnly ? 'Open a meeting' : 'Meeting filters'}>
      <div className={styles.bar}>
        <div
          className={styles.searchArea}
          ref={wrapper}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) closePicker()
          }}
        >
          <div className={styles.searchRow}>
            <Search size={17} aria-hidden="true" />
            {field && (
              <button
                type="button"
                className={styles.fieldBack}
                onClick={() => {
                  setField(null)
                  setQuery('')
                  setActive(0)
                  input.current?.focus()
                }}
                aria-label="Back to all filters"
              >
                <ArrowLeft size={14} />
                {fieldLabel(field)}
              </button>
            )}
            {!meetingLookupOnly && (field ? canNegate(field) : operatorShown) && (
              <OperatorToggle
                negated={negated}
                label="Is not"
                keepInputFocus
                onToggle={() => {
                  setNegated(!negated)
                  setActive(0)
                  setOpen(true)
                  input.current?.focus()
                }}
              />
            )}
            <input
              ref={input}
              type="text"
              role="combobox"
              aria-label={meetingLookupOnly ? 'Meeting ID or URL' : 'Search meeting filters'}
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls={open ? listId : undefined}
              aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
              value={query}
              placeholder={
                field
                  ? `Search ${fieldLabel(field).toLowerCase()}…`
                  : meetingLookupOnly
                    ? 'Paste a meeting ID or URL…'
                    : 'Filter by platform, OS, participants, or paste a meeting ID…'
              }
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onKeyDown={keyDown}
              onChange={(event) => {
                const text = event.target.value.trimStart()
                if (text.startsWith('!') && (!field || canNegate(field))) {
                  setNegated(true)
                  setOperatorShown(true)
                  setQuery(text.slice(1))
                } else setQuery(event.target.value)
                setActive(0)
                setOpen(true)
              }}
            />
          </div>
          {open && (
            <div className={styles.picker}>
              <div className={styles.pickerHeading}>
                {choices[0]?.kind === 'meeting' || meetingLookupOnly
                  ? 'Open a meeting'
                  : field
                    ? `${fieldLabel(field)}${negated ? ' — exclude' : ''}`
                    : 'Choose a filter'}
              </div>
              {isLoadingOptions && (
                <p className={styles.help} role="status">
                  Loading filter options…
                </p>
              )}
              <div
                id={listId}
                role="listbox"
                aria-label={
                  choices[0]?.kind === 'meeting'
                    ? 'Open meeting'
                    : field
                      ? `${fieldLabel(field)} values${negated ? ' to exclude' : ''}`
                      : `Meeting filter options${negated ? ' to exclude' : ''}`
                }
                className={styles.options}
              >
                {choices.map((choice, index) => (
                  <div
                    role="option"
                    aria-label={choice.label}
                    aria-description={choice.detail}
                    id={`${listId}-${index}`}
                    key={choiceKey(choice)}
                    aria-selected={index === activeIndex}
                    className={styles.option}
                    data-active={index === activeIndex}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => setActive(index)}
                    onClick={() => choose(choice)}
                  >
                    <span className={styles.optionLabel}>
                      {choice.kind === 'meeting' ? (
                        choice.mode === 'annotate' ? (
                          <ClipboardPen size={16} aria-hidden="true" />
                        ) : (
                          <ScanSearch size={16} aria-hidden="true" />
                        )
                      ) : (
                        <DimensionIcon dimension={choice.field} value={choice.value} />
                      )}
                      <span>
                        {choice.label}
                        {choice.detail && <small>{choice.detail}</small>}
                      </span>
                    </span>
                    {choice.kind === 'filter' &&
                      choice.value !== undefined &&
                      values[choice.field] === choice.value &&
                      (!canNegate(choice.field) || values.negate.includes(choice.field) === negated) && (
                        <Check size={15} aria-label="Applied" />
                      )}
                  </div>
                ))}
              </div>
              {choices.length === 0 && (
                <p className={styles.help} role="status">
                  {meetingLookupOnly
                    ? 'Paste a meeting ID or an admin URL ending in one to inspect or annotate it.'
                    : 'No matching filters.'}
                </p>
              )}
              {field === 'participant_source' && (
                <p className={styles.help}>Participant counts are estimates from the selected source.</p>
              )}
            </div>
          )}
        </div>
        {!meetingLookupOnly && <DateRange values={values} onApply={onApply} />}
      </div>
      {!meetingLookupOnly && (
        <AppliedFilterChips
          values={values}
          facets={facets}
          onApply={onApply}
          onReset={onReset}
          onBeforeRemove={() => {
            input.current?.focus()
            setOpen(false)
          }}
        />
      )}
    </section>
  )
}

export const AppliedFilterChips = ({
  values,
  facets = {},
  onApply,
  onReset,
  onBeforeRemove,
  compact = false,
}: {
  values: FilterValues
  onApply: (values: FilterValues) => void
  onReset?: () => void
  onBeforeRemove?: () => void
  compact?: boolean
  facets?: AttributionOverview['facets']
}) => {
  const activeFields = fields.filter((key) =>
    key === 'participant_source' ? values[key] !== 'canonical' : values[key]
  )
  if (!activeFields.length) return null
  return (
    <div
      className={`${styles.tags} ${compact ? styles.compactTags : ''}`}
      aria-label={compact ? 'Filtered meetings filters' : 'Applied filters'}
    >
      {activeFields.map((key) => (
        <span
          className={styles.tag}
          key={key}
          role="group"
          aria-label={`${fieldLabel(key)}${key === 'participant_source' ? ':' : !values.negate.includes(key) ? ' is' : ' is not'} ${valueLabel(key, values[key])}`}
        >
          <DimensionIcon dimension={key} value={values[key]} size={14} />
          {key === 'participant_source' ? (
            `${fieldLabel(key)}:`
          ) : (
            <>
              {fieldLabel(key)}
              <OperatorToggle
                negated={values.negate.includes(key)}
                label={`${fieldLabel(key)} is not`}
                onToggle={() =>
                  onApply({
                    ...values,
                    negate: values.negate.includes(key)
                      ? values.negate.filter((dimension) => dimension !== key)
                      : attributionDimensions.filter(
                          (dimension) => dimension === key || values.negate.includes(dimension)
                        ),
                  })
                }
              />
            </>
          )}
          <select
            className={styles.chipValue}
            aria-label={`Change ${fieldLabel(key)} filter`}
            value={values[key]}
            onChange={(event) => onApply({ ...values, [key]: event.target.value })}
          >
            {(key === 'participant_source'
              ? Object.keys(sourceLabels)
              : Array.from(new Set([...(fixedOptions[key] ?? facets[key] ?? []), 'unknown', values[key]]))
            ).map((value) => (
              <option key={value} value={value}>
                {valueLabel(key, value)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={styles.iconButton}
            aria-label={`Clear ${fieldLabel(key)} filter`}
            onClick={() => {
              onBeforeRemove?.()
              onApply({
                ...values,
                [key]: key === 'participant_source' ? 'canonical' : '',
                negate: values.negate.filter((dimension) => dimension !== key),
              })
            }}
          >
            <X size={13} />
          </button>
        </span>
      ))}
      {!compact && (
        <button type="button" className={styles.reset} onClick={onReset ?? (() => onApply(defaultFilterValues()))}>
          Reset filters
        </button>
      )}
    </div>
  )
}

export default FilterForm
